import {useEffect, useRef, useState} from 'react';
// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useBoolean, useOnMount} from 'shared/web';

import {GetDataIdsByQuery} from '../types/Api';
import {getFormattedSourceSearchParams} from '../utils/url/getFormattedSourceSearchParams';
import {setSourceSearchParamsByGridCode} from '../utils/url/setSourceSearchParamsByGridCode';
import {useHttpCalls} from './useHttpCalls';

const DETAIL_PAGINATION_LIMIT = 50;
export function useDetailPagination(
  onChange: (entityIndex: string, sourceSearchParams: string) => void
) {
  const [searchParams] = useSearchParams();

  // TODO: Optimize fetchIds - implement cache
  // Problems:
  // 1. RowControl: fetches on every page change, even if ID is already in ids.current
  // 2. EntityHeaderNavigation: loses ids on unmount
  // https://carvago.atlassian.net/browse/T20-107385
  const ids = useRef<GetDataIdsByQuery[]>(null);

  const [page, setPage] = useState(Number(getFormattedSourceSearchParams().rowIndex));
  const [isInvalid, invalidate, validate] = useBoolean();
  const [isLoading, startLoading, stopLoading] = useBoolean();

  const http = useHttpCalls({gridCode: getFormattedSourceSearchParams().gridCode});

  useEffect(() => {
    const newPage = getFormattedSourceSearchParams().rowIndex;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(Number(newPage));
  }, [searchParams]);

  const onPageChange = (newPage: number) => {
    const sourceSearchParams = getFormattedSourceSearchParams();

    if (isNilOrEmpty(sourceSearchParams.gridCode) || isNilOrEmpty(sourceSearchParams.queryId)) {
      invalidate();
      return;
    }

    const foundEntityId = ids.current?.find((id) => id.rowNumber === newPage)?.id;

    if (isNilOrEmpty(foundEntityId)) {
      invalidate();
      return;
    }

    const updatedSourceSearchParams = setSourceSearchParamsByGridCode(
      sourceSearchParams.gridCode,
      String(newPage),
      true
    );

    const initialPath = window.location.pathname + window.location.search;

    onChange(foundEntityId as string, updatedSourceSearchParams);

    const currentPathAfter = window.location.pathname + window.location.search;
    const wasNavigationBlocked = currentPathAfter === initialPath;

    if (wasNavigationBlocked) {
      return;
    }

    validate();
    setPage(newPage);
    fetchIds();
  };

  const fetchIds = () => {
    const sourceSearchParams = getFormattedSourceSearchParams();

    if (isNilOrEmpty(sourceSearchParams.gridCode) || isNilOrEmpty(sourceSearchParams.queryId)) {
      return;
    }

    const offset = Number(page) - Math.round(DETAIL_PAGINATION_LIMIT / 2);

    startLoading();
    http
      .getDataIdsByQuery(
        sourceSearchParams.queryId,
        offset > 0 ? offset : 0,
        DETAIL_PAGINATION_LIMIT,
        false
      )
      .then((response) => {
        ids.current = response ?? [];
      })
      .finally(() => {
        stopLoading();
      });
  };

  useOnMount(fetchIds);

  return {
    rowIndex: page,
    ids,
    onPageChange,
    isInvalid,
    isLoading,
  };
}
